import createKDTree from 'static-kdtree'
import _ from 'lodash'
import { getConsole } from 'lib/getConsole'
import { Viewport } from 'data/records'
import { List } from 'immutable'
const console = getConsole('lib', 'geoUtils')

// handy-dandy FP array access
// const head = array => array[0]
// const tail = array => array.slice(1)
// const last = array => array[array.length - 1]

// maxDistance is in decimal lat/long degrees
export const getNearestPointIndex = (troute, latlng, maxDistance) => {
  const index = getKdTreeFromRoute(troute).nn([latlng.lat, latlng.lng], maxDistance)
  return index >= 0 ? index : null
}

export const getNearestPoint = (troute, latlng, maxDistance = 0.01) => {
  const index = getNearestPointIndex(troute, latlng, maxDistance)
  return Number.isFinite(index) ? troute.trackPoints[index] : null
}

export const getKdTreeFromRoute = _.memoize(troute => {
  console.log(`getKdTreeFromRoute cache miss, troute.id=${troute.id}`)
  const points = troute.trackPoints.map(p => [p.lat, p.lng])
  return createKDTree(points)
}, troute => troute.id)

export const pointDistance = ([y1, x1], [y2, x2]) => {
  // convert to radians
  const lat1 = y1 * Math.PI / 180
  const lng1 = x1 * Math.PI / 180
  const lat2 = y2 * Math.PI / 180
  const lng2 = x2 * Math.PI / 180
  // do the thing
  return haversine(lat1, lng1, lat2, lng2)
}

// object-form of pointDistance
export const pointDistanceObject = (latLng1, latLng2) => {
  // convert to radians
  const lat1 = latLng1.lat * Math.PI / 180
  const lng1 = latLng1.lng * Math.PI / 180
  const lat2 = latLng2.lat * Math.PI / 180
  const lng2 = latLng2.lng * Math.PI / 180
  // do the thing
  return haversine(lat1, lng1, lat2, lng2)
}

export const pointAverageObject = (latLngs) => {
  // short-circuit if we're averaging a one-point array
  if (latLngs.length === 1) {
    const { lat, lng } = latLngs[0]
    return { lat, lng }
  }
  // convert to radians for use with JS math functions
  const inRadians = latLngs.map(latLng => ({
    lat: latLng.lat * Math.PI / 180,
    lng: latLng.lng * Math.PI / 180
  }))
  // convert to cartesian vectors
  const asVectors = inRadians.map(latLng => ({
    x: Math.cos(latLng.lat) * Math.cos(latLng.lng),
    y: Math.cos(latLng.lat) * Math.sin(latLng.lng),
    z: Math.sin(latLng.lat)
  }))

  // compute average of all vectors
  const averageVal = (key) => (
    asVectors.reduce((a, b) => a + b[key], 0) / latLngs.length
  )
  const X = averageVal('x')
  const Y = averageVal('y')
  const Z = averageVal('z')

  // Convert average X, Y, Z cartesian vector to back to latitude and longitude.
  const lng = Math.atan2(Y, X)
  const hyp = Math.sqrt(X * X + Y * Y)
  const lat = Math.atan2(Z, hyp)

  // Return result in degrees
  return {
    lat: lat / (Math.PI / 180),
    lng: lng / (Math.PI / 180)
  }
}

// wants radians
export const haversine = (lat1, lng1, lat2, lng2) => {
  const latDiff = lat1 - lat2
  const lngDiff = lng1 - lng2
  const radius = 6378100
  const latTmp = Math.sin(latDiff / 2)
  const lngTmp = Math.sin(lngDiff / 2)
  const a = latTmp * latTmp + Math.cos(lat1) * Math.cos(lat2) * lngTmp * lngTmp
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return radius * c
}

export const isValidLatLng = ll => (
  ll.lat && ll.lng &&
  ll.lat >= -90 && ll.lat <= 90 &&
  ll.lng >= -180 && ll.lng <= 180
)

export const isValidHighlightSlice = (hs, trackPoints) => (
  hs &&
  trackPoints &&
  hs.length === 2 &&
  Number.isFinite(hs[0]) && Number.isSafeInteger(hs[0]) &&
  Number.isFinite(hs[1]) && Number.isSafeInteger(hs[1]) &&
  hs[0] >= 0 &&
  hs[1] > hs[0] &&
  hs[1] <= trackPoints.length
)

// points aren't sorted by north, so our best runtime here is going to be
// O(n)
export const northernmostPointIndex = (points) => (
  points.reduce((northernmostIndex, currentPoint, currentIndex) => (
    currentPoint.lat > points[northernmostIndex].lat
      ? currentIndex
      : northernmostIndex
  ), 0)
)

export const northernmostPoint = (points) => points[northernmostPointIndex(points)]

export const worldVp = Viewport({ bounds: [
  { lat: 90, lng: 180 },
  { lat: -90, lng: -180 }
] })

export const initialViewportForTRoute = troute => {
  if (!troute || !troute.boundingBox) {
    return worldVp
  }

  const trouteVp = Viewport({ bounds: troute.boundingBox })
  if (_.isEqual(trouteVp, worldVp) || (!Viewport.validate(trouteVp) && troute.trackPoints.length > 0)) {
    return Viewport.containingAll(List([troute]))
  } else {
    return trouteVp
  }
}

export const validLatLng = ({ lat, lng }) => (
  lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
)
